import authenticatedAxios from '@/lib/axios';
import { quickLog } from '@/utils';
import { USE_MOCK_DATA } from '@/constants/config';
import { mockApiUser, mockApiUsers } from '@/mocks';
import { ApiUser, ApiUserCertificationBadge } from '@/types';
import { FEATURE_FLAGS } from '@/constants';
import { cachedGet, DEFAULT_TTL_MS } from './cache';

/**
 * Users API Service
 * Handles all API calls related to users
 */

/**
 * Get current user info (from SSO)
 */
export const getCurrentUser = async (userId: string): Promise<ApiUser> => {
  if (USE_MOCK_DATA) {
    return { ...mockApiUser, id: userId };
  }

  const endpoint = `app/users/${userId}`;
  return cachedGet(`users.current:${userId}`, async () => {
    const response = await authenticatedAxios.get<ApiUser>(endpoint);
    return response.data;
  }, DEFAULT_TTL_MS);
};

/**
 * Get user by email
 */
export const getUserByEmail = async (email: string): Promise<ApiUser> => {
  if (USE_MOCK_DATA) {
    return { ...mockApiUser, email };
  }

  const endpoint = `app/users/email/${email}`;
  return cachedGet(`users.email:${email}`, async () => {
    const response = await authenticatedAxios.get<ApiUser>(endpoint);
    return response.data;
  }, DEFAULT_TTL_MS);
};

/**
 * Get user certification badges
 */
export const getUserCertificationBadges = async (userId: string): Promise<ApiUserCertificationBadge[]> => {
  if (USE_MOCK_DATA) {
    return [];
  }

  const endpoint = `/app/user-cert-badges/user/${userId}`;
  return cachedGet(`users.badges:${userId}`, async () => {
    const response = await authenticatedAxios.get<ApiUserCertificationBadge[]>(endpoint);
    return response.data;
  }, DEFAULT_TTL_MS);
}

/**
 * Get all users
 */
export const getUsersByManagerId = async (managerId: string): Promise<ApiUser[]> => {
  if (USE_MOCK_DATA) {
    quickLog.api('[MOCK DATA] Returning mock users');
    return mockApiUsers;
  }

  if (!FEATURE_FLAGS.ENABLE_MANAGER_DASHBOARD) {
    quickLog.api('Manager Dashboard feature is disabled. Returning empty user list.');
    return [];
  }

  const endpoint = `app/users/manager/${managerId}/managed-users`;
  return cachedGet(`users.manager:${managerId}`, async () => {
    const response = await authenticatedAxios.get<ApiUser[]>(endpoint);
    return response.data;
  }, DEFAULT_TTL_MS);
};
